import { StateProvider, StateParams, StateService } from "@uirouter/angularjs";
import {
  DepreciationViewController,
  HtmlTemplate as DepreciationViewHtmlTemplate,
} from "./views/Depreciation";
import {
  DepreciationDefaultSettingViewController,
  HtmlTemplate as DepreciationDefaultSettingViewHtmlTemplate,
} from "./views/DepreciationDefaultSetting";

import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../records/server/serverUtility";

import { vsComponentName as DepreciationEditComponentName } from "./views/machineDetail/Depreciation/DepreciationEdit/component";
import { vsName as DepreciationComponentName } from "./views/machineDetail/Depreciation/component";
import { ServiceResponse } from "../records/server/query";
import { IFieldArray } from "AngularFormly";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../records/forms/formView";
import { MachineConfiguration } from "../machines/shared/models/machineConfiguration";
import { Machine } from "../machines/shared/models/machine";

export function routeConfig($stateProvider: StateProvider) {
  $stateProvider
    .state("main.depreciation", {
      url: "depreciation",
      template: "<ui-view></ui-view>",
      abstract: true,
    })
    .state("main.depreciation.grid", {
      url: "/grid",
      controller: DepreciationViewController,
      controllerAs: "stateController",
      template: DepreciationViewHtmlTemplate,
    })
    .state("main.depreciation.default", {
      url: "/Setting",
      controller: DepreciationDefaultSettingViewController,
      controllerAs: "stateController",
      template: DepreciationDefaultSettingViewHtmlTemplate,
      resolve: {
        model: [
          ServerUtilityName,
          function (serverUtility: ServerUtility) {
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: "/api/machines/depreciation/defaultSettings",
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
      },
    })
    .state("main.depreciation.Edit", {
      url: "/Edit/{machineId:int}",
      component: DepreciationEditComponentName,
      resolve: {
        returnSubmitState: [() => "^.grid"],
        returnCancelState: [() => "^.grid"],
        defaultSetting: [
          ServerUtilityName,
          function (serverUtility: ServerUtility) {
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: "/api/machines/depreciation/defaultSettings",
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
        depreciation: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const machineId = $stateParams["machineId"];
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: `api/machines/Machine/${machineId}/depreciation`,
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
      },
    })
    .state("main.depreciation.info", {
      url: "/info/{machineId:int}",
      component: DepreciationComponentName,
      resolve: {
        depreciation: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const machineId = $stateParams["machineId"];
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: `api/machines/Machine/${machineId}/depreciation`,
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
        machine: [
          ServerUtilityName,
          "$stateParams",
          function(serverUtility: ServerUtility, $stateParams: StateParams){
            const machineId = $stateParams["machineId"];
            return serverUtility.sendApiRequest<ServiceResponse<Machine<MachineConfiguration>>>({
              method: "get",
              url: `api/machines/Machine/${machineId}`,
            }).then(r => r.Result);
          }
        ],
      },
    })
    .state("main.depreciation.info.edit", {
      url: "/edit",
      component: DepreciationEditComponentName,
      resolve: {
        returnSubmitState: [() => "^.^.grid"],
        returnCancelState: [() => "^.^.grid"],
        defaultSetting: [
          ServerUtilityName,
          function (serverUtility: ServerUtility) {
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: "/api/machines/depreciation/defaultSettings",
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
        depreciation: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const machineId = $stateParams["machineId"];
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: `api/machines/Machine/${machineId}/depreciation`,
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
      },
    })
    .state("main.depreciation.Add", {
      url: "/Add",
      component: DepreciationEditComponentName,
      resolve: {
        returnSubmitState: [() => "^.grid"],
        returnCancelState: [() => "^.grid"],
        defaultSetting: [
          ServerUtilityName,
          function (serverUtility: ServerUtility) {
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: "/api/machines/depreciation/defaultSettings",
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
      },
    })
    .state("main.depreciation.default.edit", {
      url: "/edit",
      component: "recordFormView",
      resolve: {
        title: [() => "Depreciation Default Setting"],
        onSubmit: [
          "$state",
          FormViewServiceName,
          ($state: StateService, formViewService: FormViewService) =>
            async (
              formViewController: FormViewController,
              model: any
            ) => {
              await formViewService.submitForm({
                url: `/api/machines/depreciation/defaultSettings`,
                formObject: model,
                formViewController,
                method: "post",
              });
              await $state.go("^", undefined, { reload: true });
            },
        ],
        onCancel: [
          "$state",
          ($state: StateService) => async () => {
            await $state.go("^");
          },
        ],
        fields: [
          (): IFieldArray => {
            return [
              {
                key: "UsefulLife",
                name: "UsefulLife",
                type: "input",
                templateOptions: {
                  label: "Useful Life",
                  required: true,
                },
              },

              {
                key: "DepreciationMethod",
                name: "DepreciationMethod",
                type: "select",
                templateOptions: {
                  label: "Depreciation Method",
                  options: [
                    {
                      label: "Straight Line",
                      value: "StraightLine",
                    },
                    {
                      label: "Double Declining Balance",
                      value: "DoubleDecliningBalance",
                    },
                    {
                      label: "Sum Of The Years Digits",
                      value: "SumOfTheYearsDigits",
                    },
                  ],
                  required: true,
                  labelProp: "label",
                  valueProp: "value",
                },
              },
            ];
          },
        ],
        model: [
          ServerUtilityName,
          function (serverUtility: ServerUtility) {
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: "/api/machines/depreciation/defaultSettings",
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
      },
    });
}
routeConfig.$inject = ["$stateProvider"];
